import { TOCNode } from '../../../types/DocumentTypes';

export const blockIdInsideChapter = (blockId: number) => (chapter: TOCNode) =>
  chapter.blockId <= blockId &&
  chapter.blockId + chapter.blockCount - 1 >= blockId;

export const matchingNodeId = (nodeId: number) => (chapter: TOCNode) => {
  return chapter.nodeId === nodeId;
};

function flattenChapters(chapters: TOCNode[]): TOCNode[] {
  return chapters.flatMap((chapter) => [
    chapter,
    ...flattenChapters(chapter.children || []),
  ]);
}

export function findChapterByNodeId(
  chapters: TOCNode[],
  nodeId: number,
): TOCNode | null {
  const flattenedChapters = flattenChapters(chapters);
  const chapter = flattenedChapters.find(matchingNodeId(nodeId));

  if (!chapter) {
    return null;
  }

  return chapter;
}

export function findChapterRecursively(
  chapters: TOCNode[],
  comparisonFn: (chapter: TOCNode) => boolean,
): TOCNode | null {
  const chapter = chapters.find(comparisonFn);
  if (!chapter) {
    return null;
  }

  if (chapter.children && chapter.children.length > 0) {
    const child = findChapterRecursively(chapter.children, comparisonFn);

    if (child) {
      return child;
    }
  }

  return chapter;
}

/**
 * Finds the root node for any given TOCNode by traversing up the tree
 * using parentNodeId until it finds a node with parentNodeId: null
 *
 * @param nodeId - The nodeId of the TOCNode to find the root for
 * @param tocTree - The complete TOC tree array
 * @returns The root TOCNode, or null if not found
 */
export function findRootChapter(
  node: TOCNode,
  tocTree: TOCNode[],
): TOCNode | null {
  // If the target node is already a root node, return it
  if (node.parentNodeId === null) {
    return node;
  }

  const parentNode = findNodeById(node.parentNodeId, tocTree);

  if (!parentNode) {
    return null;
  }

  // Recursively find the root by following parentNodeId
  return findRootChapter(parentNode, tocTree);
}

/**
 * Helper function to find a node by its nodeId in the tree
 *
 * @param nodeId - The nodeId to search for
 * @param nodes - Array of TOCNodes to search in
 * @returns The found TOCNode or null
 */
function findNodeById(nodeId: number, nodes: TOCNode[]): TOCNode | null {
  for (const node of nodes) {
    if (node.nodeId === nodeId) {
      return node;
    }

    // Recursively search in children
    if (node.children) {
      const found = findNodeById(nodeId, node.children);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

export function findNextRootChapter(
  currentChapter: TOCNode | null,
  chapters: TOCNode[] | null,
  direction: 'next' | 'previous',
): TOCNode | null {
  // We can't find next root chapter if we don't have a current chapter or the chapters list.
  if (!currentChapter || !chapters) {
    return null;
  }

  const currentRootChapter = findRootChapter(currentChapter, chapters);

  // Couldn't find the root chapter, so we can't find the next chapter
  if (!currentRootChapter) {
    return null;
  }

  const currentIndex = chapters.findIndex(
    (chapter) => chapter.nodeId === currentRootChapter.nodeId,
  );

  if (currentIndex === -1) {
    return null;
  }

  const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
  const nextRootChapter = chapters[nextIndex];

  if (!nextRootChapter) {
    // We've reached the end of the chapters list, so we can't find the next chapter.
    return null;
  }

  // Make sure we return the root chapter
  if (nextRootChapter.parentNodeId === null) {
    return nextRootChapter;
  } else {
    return findRootChapter(nextRootChapter, chapters);
  }
}
